import { type Address, type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { callContractMethod, estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import type { TransactionHash } from "@/types";

export interface ExpectedVote {
    farm: Address;
    weight: bigint;
}

/**
 * Prepares a vote transaction
 */
export const prepareVoteTransaction = async (
    asset: Address,
    unwindingEpochs: number,
    liquidVotes: ExpectedVote[],
    illiquidVotes: ExpectedVote[],
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "vote",
            args: [asset, unwindingEpochs, liquidVotes, illiquidVotes],
        }),
    };
};

/**
 * Estimates gas for a vote transaction
 */
export const estimateVoteTransactionGas = async (
    asset: Address,
    unwindingEpochs: number,
    liquidVotes: ExpectedVote[],
    illiquidVotes: ExpectedVote[],
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareVoteTransaction(asset, unwindingEpochs, liquidVotes, illiquidVotes, walletClient);
    return await estimateContractTransactionGas(walletClient, txRequest);
};

export const simulateVoteTransaction = async (
    asset: Address,
    unwindingEpochs: number,
    liquidVotes: ExpectedVote[],
    illiquidVotes: ExpectedVote[],
    walletClient: WalletClient,
): Promise<boolean> => {
    const txRequest = await prepareVoteTransaction(asset, unwindingEpochs, liquidVotes, illiquidVotes, walletClient);
    const result = await callContractMethod(walletClient, txRequest);
    return result === "0x";
};

/**
 * Executes a vote transaction
 */
export const gatewayVoteTransaction = async (
    asset: Address,
    unwindingEpochs: number,
    liquidVotes: ExpectedVote[],
    illiquidVotes: ExpectedVote[],
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareVoteTransaction(asset, unwindingEpochs, liquidVotes, illiquidVotes, walletClient);
    return await writeContractTransaction(walletClient, txRequest);
};
